import { LOCAL_AGENT_PROTOCOL_VERSION } from '@/features/assistant/bridge/local-agent-protocol'
import { createLogger } from '@/shared/logging/logger'
import { getMcpToolCatalog, invokeMcpTool } from './mcp-tool-adapter'
import {
  getMcpResourceCatalog,
  readMcpResource,
  type McpResourceDescriptor,
  type McpResourceReadResult,
} from './mcp-resource-adapter'
import {
  getMcpPromptCatalog,
  getMcpPrompt,
  type McpPromptDescriptor,
  type McpPromptGetResult,
} from './mcp-prompt-adapter'
import type { McpToolDescriptor, McpToolResultEnvelope } from './mcp-serialization'

const logger = createLogger('VideoCutMcpServer')

type McpRequestId = string | number | null

export interface McpJsonRpcRequest<TParams = unknown> {
  jsonrpc: '2.0'
  id: McpRequestId
  method:
    | 'initialize'
    | 'ping'
    | 'tools/list'
    | 'tools/call'
    | 'resources/list'
    | 'resources/read'
    | 'prompts/list'
    | 'prompts/get'
    | 'sampling/createMessage'
  params?: TParams
}

export interface McpJsonRpcNotification<TParams = unknown> {
  jsonrpc: '2.0'
  method: string
  params?: TParams
}

export interface McpJsonRpcError {
  code: number
  message: string
  data?: unknown
}

export interface McpJsonRpcSuccessResponse<TResult = unknown> {
  jsonrpc: '2.0'
  id: McpRequestId
  result: TResult
}

export interface McpJsonRpcErrorResponse {
  jsonrpc: '2.0'
  id: McpRequestId
  error: McpJsonRpcError
}

export type McpJsonRpcResponse<TResult = unknown> =
  | McpJsonRpcSuccessResponse<TResult>
  | McpJsonRpcErrorResponse

export interface McpInitializeResult {
  protocolVersion: string
  serverInfo: {
    name: string
    version: string
  }
  capabilities: {
    tools: {
      listChanged: boolean
    }
    resources: {
      listChanged: boolean
    }
    prompts: {
      listChanged: boolean
    }
    sampling?: {
      createMessage: boolean
    }
  }
}

export interface McpToolsListResult {
  tools: McpToolDescriptor[]
}

export interface McpResourcesListResult {
  resources: McpResourceDescriptor[]
}

export interface McpPromptsListResult {
  prompts: McpPromptDescriptor[]
}

export interface McpToolsCallParams {
  name: string
  arguments?: Record<string, unknown>
}

export interface McpResourceReadParams {
  uri: string
}

export interface McpPromptGetParams {
  name: string
  arguments?: Record<string, unknown>
}

export interface McpSamplingCreateMessageParams {
  messages: Array<{
    role: 'user' | 'assistant' | 'system'
    content: Array<{
      type: 'text'
      text: string
    }>
  }>
  systemPrompt?: string
  temperature?: number
  maxTokens?: number
}

export interface McpSamplingCreateMessageResult {
  role: 'assistant'
  content: Array<{
    type: 'text'
    text: string
  }>
}

export interface McpServerToolCallContext {
  requestId: McpRequestId
}

export interface VideoCutMcpServerOptions {
  listTools?: () => McpToolDescriptor[]
  callTool?: (
    name: string,
    arguments_: Record<string, unknown> | undefined,
    context: McpServerToolCallContext,
  ) => Promise<McpToolResultEnvelope>
  listResources?: () => McpResourceDescriptor[]
  readResource?: (uri: string) => Promise<McpResourceReadResult>
  listPrompts?: () => McpPromptDescriptor[]
  getPrompt?: (
    name: string,
    arguments_: Record<string, unknown> | undefined,
  ) => Promise<McpPromptGetResult>
  createSamplingMessage?: (
    params: McpSamplingCreateMessageParams,
  ) => Promise<McpSamplingCreateMessageResult>
}

function createSuccessResponse<TResult>(
  id: McpRequestId,
  result: TResult,
): McpJsonRpcSuccessResponse<TResult> {
  return {
    jsonrpc: '2.0',
    id,
    result,
  }
}

function createErrorResponse(id: McpRequestId, error: McpJsonRpcError): McpJsonRpcErrorResponse {
  return {
    jsonrpc: '2.0',
    id,
    error,
  }
}

function validateCallParams(params: unknown): params is McpToolsCallParams {
  return (
    !!params &&
    typeof params === 'object' &&
    typeof (params as McpToolsCallParams).name === 'string'
  )
}

function validateResourceReadParams(params: unknown): params is McpResourceReadParams {
  return !!params && typeof params === 'object' && typeof (params as McpResourceReadParams).uri === 'string'
}

function validatePromptGetParams(params: unknown): params is McpPromptGetParams {
  return !!params && typeof params === 'object' && typeof (params as McpPromptGetParams).name === 'string'
}

function validateSamplingCreateMessageParams(
  params: unknown,
): params is McpSamplingCreateMessageParams {
  return !!params && typeof params === 'object' && Array.isArray((params as McpSamplingCreateMessageParams).messages)
}

export class VideoCutMcpServer {
  private readonly listToolsImpl: () => McpToolDescriptor[]
  private readonly callToolImpl: (
    name: string,
    arguments_: Record<string, unknown> | undefined,
    context: McpServerToolCallContext,
  ) => Promise<McpToolResultEnvelope>
  private readonly listResourcesImpl: () => McpResourceDescriptor[]
  private readonly readResourceImpl: (uri: string) => Promise<McpResourceReadResult>
  private readonly listPromptsImpl: () => McpPromptDescriptor[]
  private readonly getPromptImpl: (
    name: string,
    arguments_: Record<string, unknown> | undefined,
  ) => Promise<McpPromptGetResult>
  private readonly createSamplingMessageImpl?: (
    params: McpSamplingCreateMessageParams,
  ) => Promise<McpSamplingCreateMessageResult>

  constructor(options: VideoCutMcpServerOptions = {}) {
    this.listToolsImpl = options.listTools ?? getMcpToolCatalog
    this.callToolImpl =
      options.callTool ?? ((name, arguments_, _context) => invokeMcpTool(name, arguments_))
    this.listResourcesImpl = options.listResources ?? getMcpResourceCatalog
    this.readResourceImpl = options.readResource ?? readMcpResource
    this.listPromptsImpl = options.listPrompts ?? getMcpPromptCatalog
    this.getPromptImpl = options.getPrompt ?? ((name, arguments_) => getMcpPrompt(name, arguments_))
    this.createSamplingMessageImpl = options.createSamplingMessage
  }

  async initialize(): Promise<McpInitializeResult> {
    return {
      protocolVersion: LOCAL_AGENT_PROTOCOL_VERSION,
      serverInfo: {
        name: 'videocut-local-first',
        version: '2026.04.06',
      },
      capabilities: {
        tools: {
          listChanged: false,
        },
        resources: {
          listChanged: false,
        },
        prompts: {
          listChanged: false,
        },
        sampling: this.createSamplingMessageImpl ? { createMessage: true } : undefined,
      },
    }
  }

  async ping(): Promise<Record<string, never>> {
    return {}
  }

  async listTools(): Promise<McpToolsListResult> {
    return {
      tools: this.listToolsImpl(),
    }
  }

  async listResources(): Promise<McpResourcesListResult> {
    return {
      resources: this.listResourcesImpl(),
    }
  }

  async readResource(params: McpResourceReadParams): Promise<McpResourceReadResult> {
    return this.readResourceImpl(params.uri)
  }

  async listPrompts(): Promise<McpPromptsListResult> {
    return {
      prompts: this.listPromptsImpl(),
    }
  }

  async getPrompt(params: McpPromptGetParams): Promise<McpPromptGetResult> {
    return this.getPromptImpl(params.name, params.arguments)
  }

  async createSamplingMessage(
    params: McpSamplingCreateMessageParams,
  ): Promise<McpSamplingCreateMessageResult> {
    if (!this.createSamplingMessageImpl) {
      throw new Error('Sampling is not available.')
    }

    return this.createSamplingMessageImpl(params)
  }

  async callTool(
    params: McpToolsCallParams,
    context: McpServerToolCallContext,
  ): Promise<McpToolResultEnvelope> {
    return this.callToolImpl(params.name, params.arguments, context)
  }

  async handleRequest(
    request: McpJsonRpcRequest,
  ): Promise<
    McpJsonRpcResponse<
      | McpInitializeResult
      | McpToolsListResult
      | McpToolResultEnvelope
      | McpResourcesListResult
      | McpResourceReadResult
      | McpPromptsListResult
      | McpPromptGetResult
      | McpSamplingCreateMessageResult
      | Record<string, never>
    >
  > {
    try {
      switch (request.method) {
        case 'initialize':
          return createSuccessResponse(request.id, await this.initialize())
        case 'ping':
          return createSuccessResponse(request.id, await this.ping())
        case 'tools/list':
          return createSuccessResponse(request.id, await this.listTools())
        case 'resources/list':
          return createSuccessResponse(request.id, await this.listResources())
        case 'prompts/list':
          return createSuccessResponse(request.id, await this.listPrompts())
        case 'resources/read':
          if (!validateResourceReadParams(request.params)) {
            return createErrorResponse(request.id, {
              code: -32602,
              message: 'Invalid params for resources/read.',
              data: request.params,
            })
          }
          return createSuccessResponse(request.id, await this.readResource(request.params))
        case 'prompts/get':
          if (!validatePromptGetParams(request.params)) {
            return createErrorResponse(request.id, {
              code: -32602,
              message: 'Invalid params for prompts/get.',
              data: request.params,
            })
          }
          return createSuccessResponse(request.id, await this.getPrompt(request.params))
        case 'tools/call':
          if (!validateCallParams(request.params)) {
            return createErrorResponse(request.id, {
              code: -32602,
              message: 'Invalid params for tools/call.',
              data: request.params,
            })
          }

          return createSuccessResponse(
            request.id,
            await this.callTool(request.params, {
              requestId: request.id,
            }),
          )
        case 'sampling/createMessage':
          if (!validateSamplingCreateMessageParams(request.params)) {
            return createErrorResponse(request.id, {
              code: -32602,
              message: 'Invalid params for sampling/createMessage.',
              data: request.params,
            })
          }
          return createSuccessResponse(request.id, await this.createSamplingMessage(request.params))
        default:
          return createErrorResponse(request.id, {
            code: -32601,
            message: `Method not found: ${request.method}`,
          })
      }
    } catch (error) {
      logger.error('MCP request failed', error)
      return createErrorResponse(request.id, {
        code: -32000,
        message: error instanceof Error ? error.message : String(error),
      })
    }
  }
}
